import {useEffect, useState} from 'react'
import {Form, InputNumber, Select} from 'antd'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SecondaryButton from 'src/components/elements/Buttons/SecondaryButton/SecondaryButton'
import DialogCustom from 'src/components/elements/DialogCustom/DialogCustom'
import apiClient from 'src/services/apiClient'
import {STATUS} from 'src/constants/constants'
import {Typography} from '@mui/material'

type MaterialRequestDialogProps = {
  onClose: () => void;
  onSuccess: () => void;
};

const MaterialRequestDialog: React.FC<MaterialRequestDialogProps> = ({onClose, onSuccess}) => {
  const [form] = Form.useForm()
  const [materials, setMaterials] = useState<Record<string, any>[]>([])
  const [selectedMaterial, setSelectedMaterial] = useState<Record<string, any> | undefined>(undefined)
  const [statusLoad, setStatusLoad] = useState<any>(undefined)
  const [statusPost, setStatusPost] = useState<any>(undefined)

  useEffect(() => {
    const fetchMaterials = async () => {
      const result = await apiClient.getRequestableMaterials({}, setStatusLoad) ?? []
      setMaterials(result)
    }
    fetchMaterials()
  }, [])

  const onMaterialChange = (materialId: string) => {
    const material = materials.find((m) => m._id === materialId)
    setSelectedMaterial(material)
    form.setFieldValue('requestedQuantity', undefined)
  }

  const onFinish = async (values: any) => {
    if (!selectedMaterial) { return }
    await apiClient.postMaterialRequest(
      {
        materialId: selectedMaterial._id,
        materialName: selectedMaterial.name,
        code: selectedMaterial.code,
        requestedQuantity: values.requestedQuantity
      },
      setStatusPost
    )
    if (statusPost !== STATUS.REJECTED) {
      onSuccess()
    }
  }

  const getContainer = () => document.body

  const materialOptions = materials.map((m) => ({
    label: `${m.name}`,
    value: m._id
  }))

  const quantityRules: any[] = [
    {required: true, message: 'Campo obbligatorio'},
    ...(selectedMaterial && selectedMaterial.quantityInputType !== 'SELECT' ?
      [{type: 'number', max: selectedMaterial.amount, message: `La quantità massima selezionabile per questo materiale è ${selectedMaterial.amount}.`}] :
      [])
  ]

  const quantityField = selectedMaterial ? (
    selectedMaterial.quantityInputType === 'SELECT' ? (
      <Select
        dropdownStyle={{zIndex: 1400}}
        getPopupContainer={getContainer}
        options={(selectedMaterial.quantityOptions ?? []).map((v: any) => ({label: String(v), value: v}))}
        placeholder='Seleziona quantità'
        style={{width: '100%'}}
      />
    ) : (
      <InputNumber min={1} style={{width: '100%'}} />
    )
  ) : null

  const body = (
    <div>
      <Form
        form={form}
        id='material-request-form'
        layout='vertical'
        onFinish={onFinish}
      >
        <Form.Item
          label='Materiale'
          name='materialId'
          rules={[{required: true, message: 'Campo obbligatorio'}]}
        >
          <Select
            dropdownStyle={{zIndex: 1400}}
            getPopupContainer={getContainer}
            loading={statusLoad === STATUS.PENDING}
            onChange={onMaterialChange}
            options={materialOptions}
            placeholder='Seleziona un materiale'
          />
        </Form.Item>
        {selectedMaterial && (
          <Form.Item
            label='Quantità'
            name='requestedQuantity'
            rules={quantityRules}
          >
            {quantityField}
          </Form.Item>
        )}
        {statusPost === STATUS.REJECTED && (
          <Typography color='error' variant='body2'>
            {"Errore nell'invio della richiesta."}
          </Typography>
        )}
      </Form>
    </div>
  )

  const footer = (
    <div style={{display: 'flex', width: '100%'}}>
      <PrimaryButton
        form='material-request-form'
        key='submit'
        type='submit'
      >
        {'Richiedi'}
      </PrimaryButton>
      <SecondaryButton onClick={onClose}>
        {'Annulla'}
      </SecondaryButton>
    </div>
  )

  return (
    <DialogCustom
      body={body}
      footer={footer}
      maxWidth='sm'
      onClose={onClose}
      title={{text: 'Richiedi materiale', format: false}}
    />
  )
}

export default MaterialRequestDialog
